import { normalizeSafeAddress, normalizeStructTag } from "../../utils/safe-types.mjs";
import { bcs as safeBcs } from "../../bcs/index.mjs";
import { hasMvrName } from "../../client/mvr.mjs";
import { TransactionCommands } from "../Commands.mjs";
import { Inputs } from "../Inputs.mjs";
import { getClient } from "../resolve.mjs";
import { ALLOWANCE_BALANCE } from "./BalanceIntentNames.mjs";
import { resolveBalances } from "./ResolveBalances.mjs";
import { object, optional, parse, picklist, string } from "valibot";
//#region src/transactions/intents/AllowanceBalance.ts
const AllowanceBalanceData = object({
	objectId: string(),
	funder: optional(string()),
	appType: optional(string()),
	type: string(),
	amount: string(),
	outputKind: picklist(["coin", "balance"])
});
const AllowanceMetadata = safeBcs.struct("AllowanceMetadata", {
	id: safeBcs.Address,
	funder: safeBcs.Address,
	spender: safeBcs.option(safeBcs.Address),
	app: safeBcs.option(safeBcs.string())
});
function allowanceBalance({ allowance, amount, type = "0x2::safe::SAFE", outputKind }) {
	safeBcs.U64.validate(amount);
	const objectId = normalizeSafeAddress(typeof allowance === "string" ? allowance : allowance.objectId);
	const reference = typeof allowance === "string" ? { objectId } : allowance;
	return (tx) => {
		tx.addIntentResolver(ALLOWANCE_BALANCE, resolveBalances);
		return tx.add(TransactionCommands.Intent({
			name: ALLOWANCE_BALANCE,
			inputs: {
				allowance: tx.object(objectId),
				clock: tx.object.clock(),
				...reference.app ? { permit: tx.object(reference.app.permit) } : {}
			},
			data: {
				objectId,
				funder: reference.funder ? normalizeSafeAddress(reference.funder) : void 0,
				appType: reference.app ? normalizeStructTag(reference.app.type) : void 0,
				type: normalizeStructTag(type),
				amount: String(amount),
				outputKind
			}
		}));
	};
}
const resolveAllowanceBalance = async (transactionData, options, next) => {
	const ids = /* @__PURE__ */ new Set();
	const namedTypes = /* @__PURE__ */ new Set();
	for (const command of transactionData.commands) {
		if (command.$kind !== "$Intent" || command.$Intent.name !== "AllowanceBalance") continue;
		const data = parse(AllowanceBalanceData, command.$Intent.data);
		if (!data.funder) ids.add(data.objectId);
		for (const type of [data.type, data.appType]) if (type && hasMvrName(type)) namedTypes.add(type);
	}
	const [resolvedTypes, objects] = await Promise.all([namedTypes.size ? getClient(options).core.mvr.resolve({ types: [...namedTypes] }) : void 0, ids.size ? getClient(options).core.getObjects({
		objectIds: [...ids],
		include: { content: true }
	}).then((result) => result.objects) : []]);
	const allowances = /* @__PURE__ */ new Map();
	for (const object of objects) {
		if (object instanceof Error) throw object;
		allowances.set(object.objectId, object);
	}
	for (let index = 0; index < transactionData.commands.length; index++) {
		const command = transactionData.commands[index];
		if (command.$kind !== "$Intent" || command.$Intent.name !== "AllowanceBalance") continue;
		const data = parse(AllowanceBalanceData, command.$Intent.data);
		if (hasMvrName(data.type)) {
			const resolved = resolvedTypes?.types[data.type];
			if (!resolved) throw new Error(`No resolution found for type: ${data.type}`);
			data.type = normalizeStructTag(resolved.type);
		}
		if (data.appType && hasMvrName(data.appType)) {
			const resolved = resolvedTypes?.types[data.appType];
			if (!resolved) throw new Error(`No resolution found for type: ${data.appType}`);
			data.appType = normalizeStructTag(resolved.type);
		}
		let funder = data.funder;
		if (!funder) {
			const allowance = allowances.get(data.objectId);
			const expectedType = normalizeStructTag(`0x2::allowance::Allowance<0x2::balance::Balance<${data.type}>>`);
			if (!allowance || allowance.type !== expectedType || allowance.owner.$kind !== "Shared") throw new Error(`Expected a shared ${expectedType} allowance at ${data.objectId}`);
			const metadata = AllowanceMetadata.parse(allowance.content);
			if (metadata.app !== null && !data.appType) throw new Error(`Allowance ${data.objectId} is app-bound; pass allowance.app with the app type and SpendPermit`);
			if (data.appType && (metadata.app === null || normalizeStructTag(metadata.app) !== data.appType)) throw new Error(`Allowance ${data.objectId} is not bound to app ${data.appType}`);
			funder = metadata.funder;
			const input = command.$Intent.inputs.allowance;
			if (input.$kind === "Input") transactionData.inputs[input.Input] = Inputs.SharedObjectRef({
				objectId: data.objectId,
				initialSharedVersion: allowance.owner.Shared.initialSharedVersion,
				mutable: true
			});
		}
		const allowanceArgument = command.$Intent.inputs.allowance;
		if (allowanceArgument.$kind === "Input") {
			const input = transactionData.inputs[allowanceArgument.Input];
			if (input.Object?.SharedObject) input.Object.SharedObject.mutable = true;
			if (input.UnresolvedObject) input.UnresolvedObject.mutable = true;
		}
		const withdrawal = transactionData.addInput("withdrawal", Inputs.FundsWithdrawal({
			reservation: {
				$kind: "MaxAmountU64",
				MaxAmountU64: data.amount
			},
			typeArg: {
				$kind: "Balance",
				Balance: data.type
			},
			withdrawFrom: {
				$kind: "SenderAllowance",
				SenderAllowance: {
					funder,
					allowance: data.objectId
				}
			}
		}));
		const commands = [TransactionCommands.MoveCall({
			target: data.appType ? "0x2::allowance::app_balance_spend" : "0x2::allowance::balance_spend",
			typeArguments: data.appType ? [data.type, data.appType] : [data.type],
			arguments: [
				command.$Intent.inputs.allowance,
				...data.appType ? [command.$Intent.inputs.permit] : [],
				withdrawal,
				command.$Intent.inputs.clock
			]
		})];
		if (data.outputKind === "coin") commands.push(TransactionCommands.MoveCall({
			target: "0x2::coin::from_balance",
			typeArguments: [data.type],
			arguments: [{
				$kind: "Result",
				Result: index
			}]
		}));
		transactionData.replaceCommand(index, commands, { NestedResult: [index + commands.length - 1, 0] });
		index += commands.length - 1;
	}
	return next();
};
//#endregion
export { ALLOWANCE_BALANCE, allowanceBalance, resolveAllowanceBalance };

//# sourceMappingURL=AllowanceBalance.mjs.map